import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Headings,
  Checkbox,
  InputField,
  Accordion,
  Paragraph,
  DatePicker,
  RetryIcon,
  ModalOverlay,
  ErrorMessage,
  Label,
} from '@sentara-health/optimahealth_web';
import {
  ClaimsFilterState,
  initialClaimsFilterState,
} from '../../../common/types/claimsFilters';
import { formatMedicalText, getUniqueParts } from 'common/utils';
import {
  ClaimResponse as Claim,
  Extension,
} from '@sentara-health/sentara-api-hooks-core';
import { getUpdatedSelection } from './parseClaimsData';

interface ClaimsFilterProps {
  visible: boolean;
  onClose: () => void;
  onApply: (filters: ClaimsFilterState) => void;
  onCancel: () => void;
  onClearAll: () => void;
  claims: Claim[];
  appliedFilters?: ClaimsFilterState;
}

const ClaimsFilter: React.FC<ClaimsFilterProps> = React.memo(({
  visible,
  onClose,
  onApply,
  onCancel,
  onClearAll,
  claims,
  appliedFilters,
}) => {
  const { t } = useTranslation();
  const [filterState, setFilterState] = useState(initialClaimsFilterState);
  const [selectedDateOption, setSelectedDateOption] = useState('');
  const [showCustomDateRange, setShowCustomDateRange] = useState(false);
  const [isFiltersCleared, setIsFiltersCleared] = useState(false);

  useEffect(() => {
    if (visible && appliedFilters) {
      setFilterState(appliedFilters);
      if (appliedFilters.selectedDate) {
        setSelectedDateOption(appliedFilters.selectedDate);
        setShowCustomDateRange(appliedFilters.selectedDate === 'custom');
      } else {
        setSelectedDateOption('');
        setShowCustomDateRange(false);
      }
    }
  }, [visible, appliedFilters]);

  const dateOptions = [
    { value: t('medicalClaims.last30Days'), label: 'last30' },
    { value: t('medicalClaims.last60Days'), label: 'last60' },
    { value: t('medicalClaims.thisYear'), label: 'thisYear' },
    { value: t('medicalClaims.customRange'), label: 'custom' },
  ];

  const statuses = [
    t('common.approved'),
    t('medicalClaims.denied'),
    t('medicalClaims.inReview'),
    t('medicalClaims.paid'),
    t('common.pending'),
  ];

  const serviceTypes = useMemo(() => {
    const typeSet = new Map<string, string>();

    (claims ?? []).forEach((claim) => {
      if (claim.type?.coding) {
        const codings = Array.isArray(claim.type.coding)
          ? claim.type.coding
          : [claim.type.coding];

        for (const coding of codings) {
          if (coding && typeof coding.display === 'string') {
            const uniqueParts = getUniqueParts(coding.display);
            for (const part of uniqueParts) {
              const normalized = part.toLowerCase();
              if (!typeSet.has(normalized)) {
                typeSet.set(normalized, part);
              }
            }
          }
        }
      }
    });

    return [ ...Array.from(typeSet.values()), t('medicalClaims.all') ];
  }, [claims, t]);

  const locations = useMemo(() => {
    const locationSet = new Set(
      (claims ?? [])
        .map(
          (c) =>
            c.extension?.find((ext: Extension) => ext.url === 'practiceName')
              ?.value ??
            c.extension?.find(
              (ext: Extension) => ext.url === 'payToProviderName'
            )?.value
        )
        .filter(Boolean)
        .map((location) => formatMedicalText(location as string))
    );
    return [ ...Array.from(locationSet), t('medicalClaims.all')];
  }, [claims, t]);

  const providers = useMemo(() => {
    const providerSet = new Set(
      (claims ?? [])
        .map(
          (c) =>
            c.extension?.find((ext: Extension) => ext.url === 'providerName')
              ?.value
        )
        .filter(Boolean)
        .map((provider) => formatMedicalText(provider as string))
    );
    return [ ...Array.from(providerSet), t('medicalClaims.all')];
  }, [claims, t]);

  const isAmountInvalid =
    filterState.amountMin &&
    filterState.amountMax &&
    parseFloat(filterState.amountMin) > parseFloat(filterState.amountMax);

  const handleServiceTypeCheck = useCallback(
    (type: string) => {
      setFilterState((prev) => ({
        ...prev,
        selectedServiceTypes: getUpdatedSelection(
          prev.selectedServiceTypes,
          type,
          serviceTypes,
          t('medicalClaims.all')
        ),
      }));
    },
    [serviceTypes, t]
  );

  const handleStatusCheck = useCallback((status: string) => {
    setFilterState((prev) => ({
      ...prev,
      selectedStatus: prev.selectedStatus.includes(status)
        ? prev.selectedStatus.filter((statusItem) => statusItem !== status)
        : [...prev.selectedStatus, status],
    }));
  }, []);

  const handleLocationCheck = useCallback(
    (loc: string) => {
      setFilterState((prev) => ({
        ...prev,
        selectedLocations: getUpdatedSelection(
          prev.selectedLocations,
          loc,
          locations.filter(
            (locItem): locItem is string => typeof locItem === 'string'
          ),
          t('medicalClaims.all')
        ),
      }));
    },
    [locations, t]
  );

  const handleProviderCheck = useCallback(
    (prov: string) => {
      setFilterState((prev) => ({
        ...prev,
        selectedProviders: getUpdatedSelection(
          prev.selectedProviders,
          prov,
          providers.filter(
            (provItem): provItem is string => typeof provItem === 'string'
          ),
          t('medicalClaims.all')
        ),
      }));
    },
    [providers, t]
  );

  const handleDateOptionChange = useCallback((option: string) => {
    setSelectedDateOption(option);
    setShowCustomDateRange(option === 'custom');
    setFilterState((prev) => ({ ...prev, selectedDate: option }));
  }, []);

  const handleClearAll = useCallback(() => {
    onClearAll();
    setFilterState(initialClaimsFilterState);
    setIsFiltersCleared(true);
    setSelectedDateOption('');
    setShowCustomDateRange(false);
    onClose();
  }, [onClearAll, onClose]);

  const handleDateChange = useCallback(
    (field: 'customFromDate' | 'customToDate', value?: string) => {
      setFilterState((prev) => ({
        ...prev,
        [field]: value ?? ''
      }));
    },
    []
  );

  const isValidDate = (dateString: string): boolean => {
    if (!dateString) return true;
    
    const isoRegex = /^\d{4}-\d{2}-\d{2}$/;
    const usRegex = /^\d{2}\/\d{2}\/\d{4}$/;
    
    if (!isoRegex.test(dateString) && !usRegex.test(dateString)) return false;
    
    const date = new Date(dateString);
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return false;
    
    if (isoRegex.test(dateString)) {
      const [year, month, day] = dateString.split('-').map(Number);
      return date.getFullYear() === year && 
             date.getMonth() === month - 1 && 
             date.getDate() === day;
    } else if (usRegex.test(dateString)) {
      const [month, day, year] = dateString.split('/').map(Number);
      return date.getFullYear() === year && 
             date.getMonth() === month - 1 && 
             date.getDate() === day;
    }
    
    return true;
  };

  const parseDateToCompare = (dateString: string): Date | null => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
    return date;
  };

  const isFromDateInvalid = (() => {
    if (selectedDateOption !== 'custom') return {value: false};
    if (!filterState.customFromDate || filterState.customFromDate.length === 0) return {value: false};
    
    if (!isValidDate(filterState.customFromDate)) return {value: true, errorType: 'invalidDateFormat'};
    
    const fromDate = parseDateToCompare(filterState.customFromDate);
    if (!fromDate) return {value: true, errorType: 'invalidDateFormat'};
    
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    if (fromDate > today) return {value: true, errorType: 'invalidDateRange'};
    
    if (filterState.customToDate && isValidDate(filterState.customToDate)) {
      const toDate = parseDateToCompare(filterState.customToDate);
      if (toDate && fromDate > toDate) return {value: true, errorType: 'invalidDateRange'};
    }
    
    return {value: false};
  })();

  const isToDateInvalid = (() => {
    if (selectedDateOption !== 'custom') return {value: false};
    if (!filterState.customToDate || filterState.customToDate.length === 0) return {value: false};
    
    if (!isValidDate(filterState.customToDate)) return {value: true, errorType: 'invalidDateFormat'};
    
    const toDate = parseDateToCompare(filterState.customToDate);
    if (!toDate) return {value: true, errorType: 'invalidDateFormat'};
    
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    if (toDate > today) return {value: true, errorType: 'invalidDateRange'};
    
    if (filterState.customFromDate && isValidDate(filterState.customFromDate)) {
      const fromDate = parseDateToCompare(filterState.customFromDate);
      if (fromDate && toDate < fromDate) return {value: true, errorType: 'invalidDateRange'};
    }
    
    return {value: false};
  })();

  const isCustomDateIncompleteErrorMsg = 
    selectedDateOption === 'custom' && 
    showCustomDateRange &&
    (!filterState.customFromDate || !filterState.customToDate) &&
    (filterState.customFromDate || filterState.customToDate) &&
    !isFromDateInvalid.value && 
    !isToDateInvalid.value;

  const isCustomDateIncomplete = 
    selectedDateOption === 'custom' && 
    (!filterState.customFromDate || !filterState.customToDate);

  const isApplyButtonDisabled =
    !(
      filterState.selectedServiceTypes?.length ||
      filterState.selectedStatus?.length ||
      filterState.selectedLocations?.length ||
      filterState.selectedProviders?.length ||
      filterState.selectedDate ||
      filterState.customFromDate ||
      filterState.customToDate ||
      filterState.amountMin ||
      filterState.amountMax
    ) || isAmountInvalid || isCustomDateIncomplete || isFromDateInvalid.value || isToDateInvalid.value;

  const renderServiceType = () => (
    <div className="scroll-list-md">
      {serviceTypes?.map((type, id) => (
        <div
          key={type ?? `serviceType-${id}`}
          className={`mb-2 ${id === 0 ? 'mt-2' : ''} me-3 ms-2`}
        >
          <Checkbox
            id={`serviceType-${id}`}
            type="checkbox"
            checked={
              type === t('medicalClaims.all')
                ? filterState.selectedServiceTypes?.length ===
                  serviceTypes.length - 1
                : filterState.selectedServiceTypes?.includes(type)
            }
            onChange={() => handleServiceTypeCheck(type)}
            dataTestId={`serviceType-${id}`}
          >
            {type === t('medicalClaims.all')
              ? t('medicalClaims.all')
              : type}
          </Checkbox>
        </div>
      ))}
    </div>
  );

  const renderDate = () => (
    <div className="ms-2 mb-2">
      {dateOptions.map((option, index) => (
        <div
          key={option.label}
          className={`${option.label !== 'custom' ? 'mb-2' : ''} ${
            index === 0 ? 'mt-2' : ''
          } me-3 ms-2`}
        >
          <Checkbox
            id={`dateOption-${index}`}
            type="radio"
            name="dateOption"
            checked={selectedDateOption === option.label}
            onChange={() => handleDateOptionChange(option.label)}
            dataTestId={`dateOption-${index}`}
          >
            {option.value}
          </Checkbox>
        </div>
      ))}

      {showCustomDateRange && (
        <div className="px-3 pt-2">
          <Headings
            level={4}
            text={t('medicalClaims.dateRangeTitle')}
            variant="regular"
            className="mb-1"
          />
          <div className="row">
            <div className="col-12 mb-3">
              <Label>{t('medicalClaims.from')}</Label>
              <DatePicker
                selectedValue={filterState.customFromDate}
                placeholder={t('medicalClaims.datePlaceHolder')}
                dataTestId="customFromDate"
                maxDate={new Date()}
                updateValue={(value?: string) =>
                  handleDateChange('customFromDate', value)
                }
                defaultActiveStartDate={filterState.customFromDate}
              />
              {isFromDateInvalid.value && isFromDateInvalid.errorType && (
                <Paragraph
                  text={t(`medicalClaims.${isFromDateInvalid.errorType}`)}
                  textColor="error"
                  className="mt-1"
                />
              )}
            </div>
            <div className="col-12 mb-3">
              <Label>{t('medicalClaims.to')}</Label>
              <DatePicker
                selectedValue={filterState.customToDate}
                placeholder={t('medicalClaims.datePlaceHolder')}
                dataTestId="customToDate"
                maxDate={new Date()}
                updateValue={(value?: string) =>
                  handleDateChange('customToDate', value)
                }
                defaultActiveStartDate={filterState.customToDate}
              />
              {isToDateInvalid.value && isToDateInvalid.errorType && (
                <Paragraph
                  text={t(`medicalClaims.${isToDateInvalid.errorType}`)}
                  textColor="error"
                  className="mt-1"
                />
              )}
            </div>
          </div>
          {isCustomDateIncompleteErrorMsg && (
            <Paragraph
              text={t('medicalClaims.dateRangeError')}
              textColor="error"
            />
          )}
        </div>
      )}
    </div>
  );

  const renderStatus = () => (
    <div className="ms-2 mb-2">
      {statuses.map((status, id) => (
        <div key={status} className="mb-2">
          <Checkbox
            id={`status-${id}`}
            type="checkbox"
            checked={filterState.selectedStatus?.includes(status)}
            onChange={() => handleStatusCheck(status)}
            dataTestId={`status-${id}`}
          >
            {status}
          </Checkbox>
        </div>
      ))}
    </div>
  );

  const renderLocation = () => (
    <div className="scroll-list-md">
      {locations?.map((locationItem, id) => (
        <div
          key={locationItem ?? `location-${id}`}
          className={`mb-2 ${id === 0 ? 'mt-2' : ''} me-3 ms-2 d-flex align-items-start`}
        >
          <Checkbox
            id={`location-${id}`}
            type="checkbox"
            checked={
              locationItem === t('medicalClaims.all')
                ? filterState.selectedLocations?.length === locations.length - 1
                : filterState.selectedLocations?.includes(locationItem ?? '')
            }
            onChange={() => handleLocationCheck(locationItem ?? '')}
            dataTestId={`location-${id}`}
          >
              {locationItem === t('medicalClaims.all')
                ? t('medicalClaims.all')
                : locationItem}
          </Checkbox>
        </div>
      ))}
    </div>
  );

  const handleAmountMinChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFilterState((prev) => ({ ...prev, amountMin: e.target.value }));
    },
    []
  );

  const handleAmountMaxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFilterState((prev) => ({ ...prev, amountMax: e.target.value }));
    },
    []
  );

  const handleApply = useCallback(() => {
    if (isApplyButtonDisabled) return;
    onApply(filterState);
    setIsFiltersCleared(false);
  }, [isApplyButtonDisabled, onApply, filterState]);

  const renderAmount = () => (
    <div className="m-2">
      <div className="d-flex align-items-center gap-2 px-2">
        <InputField
          type="number"
          placeholder="Min"
          value={filterState.amountMin}
          onChange={handleAmountMinChange}
          dataTestId="amountMinInput"
          className={isAmountInvalid ? 'is-invalid' : ''}
          maxLength={10}
        />
        <Paragraph text="-" variant="regular" className="m-0 p-0" />
        <InputField
          type="number"
          placeholder="Max"
          value={filterState.amountMax}
          onChange={handleAmountMaxChange}
          dataTestId="amountMaxInput"
          className={isAmountInvalid ? 'is-invalid' : ''}
          maxLength={10}
        />
      </div>
      {isAmountInvalid && (
        <ErrorMessage>{t('medicalClaims.amountMinMaxError')}</ErrorMessage>
      )}
    </div>
  );

  const renderProvider = () => (
    <div className="scroll-list-md">
      {providers?.map((providerItem, id) => (
        <div
          key={providerItem ?? `provider-${id}`}
          className={`mb-2 ${id === 0 ? 'mt-2' : ''} me-3 ms-2  d-flex align-items-start`}
        >
          <Checkbox
            id={`provider-${id}`}
            type="checkbox"
            checked={
              providerItem === t('medicalClaims.all')
                ? filterState.selectedProviders?.length === providers.length - 1
                : filterState.selectedProviders?.includes(providerItem ?? '')
            }
            onChange={() => handleProviderCheck(providerItem ?? '')}
            dataTestId={`provider-${id}`}
          >
            {providerItem === t('medicalClaims.all')
              ? t('medicalClaims.all')
              : providerItem}
          </Checkbox>
        </div>
      ))}
    </div>
  );

  const accordionItems = [
    {
      id: 'serviceType',
      title: t('medicalClaims.serviceType'),
      content: renderServiceType(),
    },
    {
      id: 'date',
      title: t('common.date'),
      content: renderDate(),
    },
    {
      id: 'location',
      title: t('medicalClaims.location'),
      content: renderLocation(),
    },
    {
      id: 'status',
      title: t('common.status'),
      content: renderStatus(),
    },
    {
      id: 'amount',
      title: t('medicalClaims.amountYouMayOwe'),
      content: renderAmount(),
    },
    {
      id: 'provider',
      title: t('medicalClaims.provider'),
      content: renderProvider(),
    },
  ];

  return (
    <ModalOverlay
      isOpen={visible}
      onClose={onClose}
      overlayInfo="Claims Filter Modal"
      className="modal-w-small p-0"
      isFiltered
    >
      <div className="d-flex gap-3 p-3">
        <Paragraph
          text={t('medicalClaims.filterTitle')}
          variant="bold"
          className="mb-0"
        />
        <Button
          variant="link_btn"
          iconLeft={<RetryIcon fill="secondary" />}
          onClick={handleClearAll}
          dataTestId="clearAllButton"
        >
          {t('medicalClaims.clearAll')}
        </Button>
      </div>

        <Accordion
          id="claimsFilterAccordion"
          items={accordionItems}
          initialActiveIndex={0}
          isFiltered
          className=""
        />

      <div className="d-flex gap-2 justify-content-center p-4 border-top">
        <Button
          variant="primary_btn"
          onClick={handleApply}
          disabled={isApplyButtonDisabled}
          dataTestId="applyButton"
        >
          {t('medicalClaims.apply')}
        </Button>
        <Button
          variant="secondary_btn"
          onClick={onCancel}
          dataTestId="cancelButton"
        >
          {t('common.cancel')}
        </Button>
      </div>
    </ModalOverlay>
  );
});

ClaimsFilter.displayName = 'ClaimsFilter';

export default ClaimsFilter;
